import { Button, ButtonProps, styled } from '@mui/material';
import { ReactElement, ElementType } from 'react';
import { useMatch } from 'react-router-dom';

export type TabButtonBaseProps = ButtonProps & {
  /** The routes in which the button should be active */
  activeRoutes?: string[];
  isActive?: boolean; // Internal only
};

/**
 * Generic type for a polymorphic TabButton.
 *
 * - Accepts MUI Button props (`ButtonProps`).
 * - Merges in props from the custom `component` passed via `component={...}`.
 * - Uses `Omit<...>` to avoid duplicate/conflicting props between MUI Button and the custom component.
 *
 * This enables usage like `component={NavLink}` and allows `to`, `end`, etc. to be type-checked.
 */
type PolymorphicTabButtonProps<C extends ElementType> = TabButtonBaseProps &
  Omit<React.ComponentPropsWithRef<C>, keyof ButtonProps> & {
    component?: C;
  };

const StyledTabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<TabButtonBaseProps>(({ theme, isActive }) => ({
  background: isActive ? theme.palette.navigation.activeBackground : theme.palette.navigation.background,
  border: `0.5px solid ${theme.palette.divider}`,
  color: isActive ? theme.palette.navigation.activeContrastText : theme.palette.navigation.contrastText,
  fontWeight: 600,
  '&:hover': {
    background: theme.palette.navigation.activeBackground,
  },
}));

export const TabButton = <C extends ElementType = 'button'>({
  activeRoutes,
  children,
  ...rest
}: PolymorphicTabButtonProps<C>): ReactElement => {
  const isActive = activeRoutes?.map((route) => useMatch(route)).some((value) => value);
  return (
    <StyledTabButton isActive={isActive} {...rest}>
      {children}
    </StyledTabButton>
  );
};
